/**
 * Typeform Response Parser
 * Dynamically parses Typeform responses based on form definitions
 * Handles any survey type without hardcoded assumptions
 */

/**
 * Parse Typeform responses based on form definition
 * @param {Object} formDefinition - The form definition from Typeform API
 * @param {Object} responses - The responses object with items array
 * @returns {Object} Parsed and organized response data
 */
export function parseTypeformResponses(formDefinition, responses) {
  if (!formDefinition || !responses || !responses.items) {
    return {
      questions: [],
      totalResponses: 0,
      nps: null,
      ratings: []
    };
  }

  // Create a map of field IDs to field definitions while preserving order
  const fieldMap = new Map();
  const fieldOrder = new Map(); // Track original position in form
  if (formDefinition.fields) {
    formDefinition.fields.forEach((field, index) => {
      fieldMap.set(field.id, {
        id: field.id,
        title: field.title,
        type: field.type,
        properties: field.properties || {},
        ref: field.ref,
        position: index // Add position tracking
      });
      fieldOrder.set(field.id, index);
    });
  }

  // Group responses by question
  const questionResponses = new Map();
  
  responses.items.forEach(response => {
    if (!response.answers) return;
    
    response.answers.forEach(answer => {
      const fieldId = answer.field.id;
      const field = fieldMap.get(fieldId);
      
      if (!field) return;
      
      if (!questionResponses.has(fieldId)) {
        questionResponses.set(fieldId, {
          field: field,
          responses: []
        });
      }
      
      // Store the answer value based on type
      let value = null;
      switch (answer.type) {
        case 'number':
          value = answer.number;
          break;
        case 'text':
          value = answer.text;
          break;
        case 'choice':
          value = answer.choice?.label || answer.choice?.other || null;
          break;
        case 'choices': {
          // For multi-select, push each label individually
          const labels = answer.choices?.labels || [];
          if (labels.length > 0) {
            labels.forEach(label => {
              questionResponses.get(fieldId).responses.push(label);
            });
          }
          
          // Also handle "Other" text responses
          const otherValue = answer.choices?.other;
          if (otherValue && otherValue.trim() !== '') {
            questionResponses.get(fieldId).responses.push(otherValue);
          }
          
          value = null; // Set to null to skip the push below
          break;
        }
        case 'boolean':
          value = answer.boolean;
          break;
        case 'email':
          value = answer.email;
          break;
        case 'url':
          value = answer.url;
          break;
        case 'date':
          value = answer.date;
          break;
        case 'phone_number':
          value = answer.phone_number;
          break;
        default:
          value = answer;
      }
      
      // Only push non-null, non-empty values
      if (value !== null && value !== undefined && 
          (typeof value !== 'object' || Object.keys(value).length > 0)) {
        questionResponses.get(fieldId).responses.push(value);
      }
    });
  });

  // Process each question type in original form order
  const processedQuestions = [];
  let npsData = null;
  const ratingQuestions = [];
  
  // Convert Map to array and sort by original position
  const sortedResponses = Array.from(questionResponses.entries())
    .sort((a, b) => {
      const positionA = fieldOrder.get(a[0]) || 0;
      const positionB = fieldOrder.get(b[0]) || 0;
      return positionA - positionB;
    });
  
  sortedResponses.forEach(([fieldId, data]) => {
    const { field, responses: answers } = data;
    
    // Classify question type (mutually exclusive)
    const questionType = classifyQuestion(field);
    
    if (questionType === 'nps') {
      npsData = calculateNPS(answers);
      npsData.question = field.title;
      npsData.fieldId = fieldId;
      
      if (import.meta.env.DEV) {
        console.log(`Classified as NPS: "${field.title}" (position ${field.position + 1})`);
      }
    } else if (questionType === 'rating') {
      if (import.meta.env.DEV) {
        console.log(`Classified as RATING: "${field.title}" (position ${field.position + 1})`);
      }
      
      // Process rating questions
      let processedAnswers = answers;
      if (field.type === 'multiple_choice') {
        processedAnswers = convertTextToNumericRatings(answers);
      }
      
      if (processedAnswers.length > 0) {
        const avgRating = calculateAverage(processedAnswers);
        ratingQuestions.push({
          question: field.title,
          fieldId: fieldId,
          rating: avgRating,
          responses: processedAnswers.length,
          type: 'rating'
        });
      }
    }
    
    // Store all questions for detailed breakdown with position info
    processedQuestions.push({
      fieldId: fieldId,
      question: field.title,
      type: field.type,
      properties: field.properties,
      responses: answers,
      responseCount: answers.length,
      analysis: analyzeResponses(field, answers),
      position: field.position || 0 // Include original position
    });
  });

  return {
    questions: processedQuestions,
    totalResponses: responses.items.length,
    nps: npsData,
    ratings: ratingQuestions,
    metadata: {
      formId: formDefinition.id,
      formTitle: formDefinition.title,
      totalQuestions: formDefinition.fields ? formDefinition.fields.filter(f => f.type !== 'statement').length : 0
    }
  };
}

/**
 * Classify question type (mutually exclusive classification)
 * @param {Object} field - Field definition
 * @returns {string} Question classification: 'nps', 'rating', 'other'
 */
function classifyQuestion(field) {
  // 1. Check for dedicated NPS type first
  if (field.type === 'nps') {
    return 'nps';
  }
  
  // 2. Check for NPS via opinion_scale with specific characteristics
  if (field.type === 'opinion_scale' || field.type === 'rating') {
    const props = field.properties || {};
    const startsAtZero = props.start_at_one !== true;
    const isZeroToTen = props.steps === 11 && startsAtZero;
    
    // NPS questions have specific keywords and 0-10 scale
    const title = field.title.toLowerCase();
    const npsKeywords = ['recommend', 'likely', 'nps'];
    const hasNPSKeyword = npsKeywords.some(keyword => title.includes(keyword));
    
    if (isZeroToTen && hasNPSKeyword) {
      return 'nps';
    }
  }
  
  // 3. Check for rating questions (must not be NPS)
  if (field.type === 'rating') {
    return 'rating';
  }
  
  // 4. Check for multiple choice rating questions
  if (field.type === 'multiple_choice') {
    const title = field.title.toLowerCase();
    const ratingKeywords = ['how relevant', 'how effective'];
    const hasRatingKeyword = ratingKeywords.some(keyword => title.includes(keyword));
    
    if (hasRatingKeyword) {
      return 'rating';
    }
  }
  
  // 5. Everything else is 'other'
  return 'other';
}

/**
 * Convert text-based multiple choice answers to numeric ratings
 * @param {Array} answers - Array of text answers
 * @returns {Array} Array of numeric values
 */
function convertTextToNumericRatings(answers) {
  return answers.map(answer => {
    if (typeof answer === 'string') {
      // Extract number from strings like "5 - Extremely Effective"
      const match = answer.match(/^(\d+)/);
      if (match) {
        return parseInt(match[1]);
      }
      
      // Map common text values to numbers (order matters - specific first)
      const textToNumberPatterns = [
        { pattern: /\bvery\s+poor\b/i, value: 1 },
        { pattern: /\bnot\s+at\s+all\b/i, value: 1 },
        { pattern: /\bextremely\b/i, value: 5 },
        { pattern: /\bexcellent\b/i, value: 5 },
        { pattern: /\bvery\b/i, value: 4 },
        { pattern: /\bgood\b/i, value: 4 },
        { pattern: /\bmoderately\b/i, value: 3 },
        { pattern: /\bfair\b/i, value: 3 },
        { pattern: /\bslightly\b/i, value: 2 },
        { pattern: /\bpoor\b/i, value: 2 }
      ];
      
      for (const { pattern, value } of textToNumberPatterns) {
        if (pattern.test(answer)) {
          return value;
        }
      }
    }
    return answer;
  }).filter(val => !isNaN(val));
}

/**
 * Calculate NPS score from responses
 * @param {Array} responses - Array of numeric responses (0-10)
 * @returns {Object} NPS data with score and breakdown
 */
export function calculateNPS(responses) {
  if (!responses || responses.length === 0) {
    return {
      score: 0,
      promoters: 0,
      passives: 0,
      detractors: 0,
      total: 0,
      promoterCount: 0,
      passiveCount: 0,
      detractorCount: 0
    };
  }

  // Keep only finite numbers in the 0–10 range
  const numeric = responses
    .map(Number)
    .filter(n => Number.isFinite(n) && n >= 0 && n <= 10);

  if (numeric.length === 0) {
    return {
      score: 0,
      promoters: 0,
      passives: 0,
      detractors: 0,
      total: 0,
      promoterCount: 0,
      passiveCount: 0,
      detractorCount: 0
    };
  }

  let promoterCount = 0;
  let passiveCount = 0;
  let detractorCount = 0;
  
  numeric.forEach(numScore => {
    if (numScore >= 9) {
      promoterCount++;
    } else if (numScore >= 7) {
      passiveCount++;
    } else {
      detractorCount++;
    }
  });
  
  const total = numeric.length;
  const npsScore = Math.round(((promoterCount - detractorCount) / total) * 100);
  
  return {
    score: npsScore,
    promoters: Math.round((promoterCount / total) * 100),
    passives: Math.round((passiveCount / total) * 100),
    detractors: Math.round((detractorCount / total) * 100),
    total: total,
    promoterCount: promoterCount,
    passiveCount: passiveCount,
    detractorCount: detractorCount
  };
}

/**
 * Calculate average of numeric responses
 * @param {Array} responses - Array of numeric responses
 * @returns {number} Average value
 */
function calculateAverage(responses) {
  if (!responses || responses.length === 0) return 0;
  
  let sum = 0;
  let numericCount = 0;
  
  responses.forEach(val => {
    const num = Number(val);
    if (!isNaN(num) && isFinite(num)) {
      sum += num;
      numericCount++;
    }
  });
  
  if (numericCount === 0) return 0;
  
  return Math.round((sum / numericCount) * 10) / 10; // Round to 1 decimal
}

/**
 * Analyze responses based on question type
 * @param {Object} field - Field definition
 * @param {Array} responses - Array of responses
 * @returns {Object} Analysis results
 */
function analyzeResponses(field, responses) {
  if (!responses || responses.length === 0) {
    return { type: 'empty', summary: 'No responses' };
  }

  switch (field.type) {
    case 'yes_no': {
      const yesCount = responses.filter(r => r === true).length;
      const noCount = responses.filter(r => r === false).length;
      return {
        type: 'yes_no',
        yes: Math.round((yesCount / responses.length) * 100),
        no: Math.round((noCount / responses.length) * 100),
        yesCount,
        noCount
      };
    }
    
    case 'multiple_choice':
    case 'dropdown': {
      const frequency = {};
      responses.forEach(response => {
        // Handle both single values and arrays (from multi-select)
        if (response === null || response === undefined) return;
        
        if (Array.isArray(response)) {
          // Multi-select: count each item individually
          response.forEach(item => {
            const key = String(item);
            frequency[key] = (frequency[key] || 0) + 1;
          });
        } else {
          // Single select: count the value
          const key = String(response);
          frequency[key] = (frequency[key] || 0) + 1;
        }
      });
      return {
        type: 'frequency',
        distribution: frequency,
        mode: Object.entries(frequency).sort((a, b) => b[1] - a[1])[0]?.[0]
      };
    }
    
    case 'opinion_scale':
    case 'rating': {
      // Filter to only finite numeric values
      const numericValues = responses
        .map(r => Number(r))
        .filter(n => isFinite(n));
      
      if (numericValues.length === 0) {
        return {
          type: 'numeric',
          average: null,
          min: null,
          max: null,
          median: null,
          validResponses: 0
        };
      }
      
      return {
        type: 'numeric',
        average: calculateAverage(numericValues),
        min: Math.min(...numericValues),
        max: Math.max(...numericValues),
        median: calculateMedian(numericValues),
        validResponses: numericValues.length
      };
    }
    
    case 'short_text':
    case 'long_text': {
      return {
        type: 'text',
        responseCount: responses.length,
        sampleResponses: responses.slice(0, 5) // First 5 responses as sample
      };
    }
    
    case 'number': {
      // For number fields, check if it looks like years (for management experience)
      const currentYear = new Date().getFullYear();
      const looksLikeYears = responses.some(r => r >= 1990 && r <= currentYear);
      
      if (looksLikeYears && field.title && field.title.toLowerCase().includes('year')) {
        // Convert years to experience ranges for better visualization
        const experienceRanges = {};
        responses.forEach(response => {
          if (response === null || response === undefined || response < 1990) return;
          
          const yearsExperience = currentYear - response;
          let range;
          
          if (yearsExperience === 0) {
            range = 'Less than 1 year';
          } else if (yearsExperience <= 2) {
            range = '1-2 years';
          } else if (yearsExperience <= 5) {
            range = '3-5 years';
          } else if (yearsExperience <= 10) {
            range = '6-10 years';
          } else if (yearsExperience <= 15) {
            range = '11-15 years';
          } else {
            range = '15+ years';
          }
          
          experienceRanges[range] = (experienceRanges[range] || 0) + 1;
        });
        
        const mode = Object.keys(experienceRanges).reduce((a, b) => 
          experienceRanges[a] > experienceRanges[b] ? a : b, Object.keys(experienceRanges)[0]
        );
        
        return {
          type: 'frequency',
          distribution: experienceRanges,
          mode,
          responseCount: responses.length,
          isExperienceData: true
        };
      }
      
      // For other number fields, create simple frequency distribution
      const frequency = {};
      responses.forEach(response => {
        if (response === null || response === undefined) return;
        const key = String(response);
        frequency[key] = (frequency[key] || 0) + 1;
      });
      
      const mode = Object.keys(frequency).reduce((a, b) => 
        frequency[a] > frequency[b] ? a : b, Object.keys(frequency)[0]
      );
      
      return {
        type: 'frequency',
        distribution: frequency,
        mode,
        responseCount: responses.length
      };
    }
    
    default: {
      return {
        type: field.type,
        responseCount: responses.length
      };
    }
  }
}

/**
 * Calculate median of numeric array
 * @param {Array} values - Array of numbers
 * @returns {number|null} Median value or null if no valid numbers
 */
function calculateMedian(values) {
  if (!values || values.length === 0) return null;
  
  // Filter to only finite numbers
  const validNumbers = values.filter(v => isFinite(v));
  if (validNumbers.length === 0) return null;
  
  const sorted = validNumbers.slice().sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  
  return sorted[middle];
}

/**
 * Get chart data for NPS visualization
 * @param {Object} npsData - NPS calculation results
 * @returns {Array} Chart data for radial bar chart
 */
export function getNPSChartData(npsData) {
  if (!npsData) {
    return [{ name: 'NPS', value: 0, fill: '#94a3b8' }];
  }
  
  const score = npsData.score || 0;
  const fill = score >= 50 ? '#10b981' : score >= 0 ? '#f59e0b' : '#ef4444';
  
  return [{
    name: 'NPS',
    value: Math.abs(score),
    fill: fill
  }];
}

/**
 * Get the actual question number (1-indexed) from parsed data, excluding statements
 * @param {string} fieldId - The field ID to find
 * @param {Object} parsedData - The parsed survey data
 * @param {Object} formDefinition - The original form definition
 * @returns {number} Question number (1-indexed) or 0 if not found
 */
export function getQuestionNumber(fieldId, parsedData, formDefinition) {
  if (!parsedData?.questions || !fieldId || !formDefinition?.fields) return 0;
  
  // Find the question by field ID
  const question = parsedData.questions.find(q => q.fieldId === fieldId);
  if (!question) return 0;
  
  // Count only non-statement questions up to this position
  const actualQuestionNumber = formDefinition.fields
    .slice(0, (question.position || 0) + 1)
    .filter(f => f.type !== 'statement').length;
    
  return actualQuestionNumber;
}

/**
 * Get question numbers for NPS and rating questions
 * @param {Object} parsedData - The parsed survey data
 * @param {Object} formDefinition - The original form definition
 * @returns {Object} Object with npsQuestionNumber and ratingQuestionNumbers
 */
export function getSpecialQuestionNumbers(parsedData, formDefinition) {
  let npsQuestionNumber = null;
  const ratingQuestionNumbers = [];
  
  if (parsedData?.nps?.fieldId) {
    npsQuestionNumber = getQuestionNumber(parsedData.nps.fieldId, parsedData, formDefinition);
  }
  
  if (parsedData?.ratings) {
    parsedData.ratings.forEach(rating => {
      if (rating.fieldId) {
        const questionNum = getQuestionNumber(rating.fieldId, parsedData, formDefinition);
        if (questionNum > 0) {
          ratingQuestionNumbers.push(questionNum);
        }
      }
    });
  }
  
  return {
    npsQuestionNumber,
    ratingQuestionNumbers: ratingQuestionNumbers.sort((a, b) => a - b)
  };
}

/**
 * Determine best visualization type for a question
 * @param {Object} field - Field definition
 * @returns {string} Recommended chart type
 */
export function getRecommendedVisualization(field) {
  const questionType = classifyQuestion(field);
  
  if (questionType === 'nps') {
    return 'nps-gauge';
  }
  
  if (questionType === 'rating') {
    return 'rating-bar';
  }
  
  switch (field.type) {
    case 'yes_no':
      return 'pie-chart';
    case 'multiple_choice':
    case 'dropdown':
      // Use pie chart for multiple choice questions (better for categorical data)
      return 'pie-chart';
    case 'opinion_scale':
      return 'bar-chart';
    case 'number':
      // Check if this looks like a year question for experience data
      if (field.title && field.title.toLowerCase().includes('year')) {
        return 'pie-chart';
      }
      return 'bar-chart';
    case 'short_text':
    case 'long_text':
      return 'text-list';
    default:
      return 'summary';
  }
}